import { formatNumber, formatCurrency } from '@atc/string-fns';
import qs from 'querystring';
import { memo, useContext, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { mapDispatchToActionProp } from '@atc/modular-redux';
import { eventBus } from '@atc/cai-event-bus';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isNumber from 'lodash/isNumber';
import isObject from 'isobject';
import clsx from 'clsx';
import { getConsumerMetadata } from '@atc/consumer-metadata';
import { sendClick } from 'reaxl-analytics';
import { ndsCalcInteraction, ndsAutoFill, ndsKBBTradeInCTAClick, ndsPayoffQuoteCTAClick, ndsVerifyCreditCTAClick, myWalletInteractionClick, ndsPencilingEvents, ndsLendingOptionsCTAClick, ndsNavigationClick } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';
import { Form } from 'reaxl-forms';
import { EmailCTA } from 'reaxl-email';
import { Heading, Text, SelectableTabPanel, SelectableTabs, LoadingSpinner, Button, Image, Glyphicon } from 'reaxl';
import { getLeaseOffer, getCashOffer, PricingBreakdown } from 'reaxl-wallet';
import { UDS_PENCILING_DATA_UPDATED, OPEN_WALLET, debugWallet, UDS_PAYMENT_CALC_RESPONSE_UPDATE, WALLET_CREATE_ID_REQUEST } from 'wallet-manager';
import { calculateDownPayment, getObfuscatedCredit, getLowestPersonalizedPayment, getCreditTierOptions, isLoanTermCustom, calculatePersonalPayment, getPersonalizedPaymentParams } from '@atc/wallet-payment-fns';
import { ratesLabels } from 'axl-config';
import ConfigContext from '../ConfigContext/ConfigContext.js';
import ndsConstants from './ndsConstants.js';
import useVehiclePaymentDetails from './hooks/useVehiclePaymentDetails.js';
import useExternalDealTypeChange from './hooks/useExternalDealTypeChange.js';
import useExternalIncentivesRequest from './hooks/useExternalIncentivesRequest.js';
import useLendingOptions from './hooks/useLendingOptions.js';
import useExternalSelectedCriteriaUpdate from './hooks/useExternalSelectedCriteriaUpdate.js';
import CallsToAction from '../CallsToAction/CallsToAction.js';
import TaxesAndFees from '../TaxesAndFees/TaxesAndFees.js';
import buildPayments from './helpers/buildPayments.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import OfferColumn from './components/OfferColumn.js';
import FinancingCallToAction from './components/FinancingCallToAction.js';
import CashTab from './components/CashTab.js';
import LeaseTab from './components/LeaseTab.js';
import FinanceTab from './components/FinanceTab.js';
const {
  defaultRateLabel: DEFAULT_RATE_LABEL,
  tabLabels: TAB_LABELS
} = ndsConstants;

/**
 *  Base Native Deal Structure calculator used on VDP
 */

function NativeDealCalculator(_ref) {
  let {
    actions: {
      updateIsLoading = () => {},
      updateListingCashPayments = () => {},
      updateListingLeasePayments = () => {},
      updateMyWallet = () => {},
      updatePaymentsInfo = () => {},
      updatePaymentPencils = () => {},
      updatePencilingPayments = () => {},
      updateMyWalletInteractions = () => {},
      updatePenciledVdpListings = () => {},
      updatePenciledLatestPageId = () => {}
    },
    additionalContent,
    brand,
    container,
    // For Syndicated Calc
    creditApp,
    customCallToAction,
    dealerFinancingCTA = {},
    dealsDuck,
    dealXgData,
    enableCreditApp: enableCreditAppCTA = false,
    enableExernalFetchForVehicle,
    env = 'staging',
    // staging or prod
    initialDealType = 'finance',
    isLoading = false,
    listing,
    listingId,
    listingIncentives = {},
    listingPaymentDetails = {},
    listingPrice: fallbackListingPrice,
    listingVin,
    onEmailCTAClick,
    onFetchPersonalizedPaymentDetails,
    onFinancingCTAClick,
    onSubmit = () => {},
    ownerCreditAppDeal,
    paymentsDuck,
    paymentsInfo,
    pencilingPaymentDetails,
    personalPaymentProps = {},
    saveDealXgFetcher,
    showNdsWithDeal,
    sponsoredInsurance,
    supportedDealTypes = ['finance', 'cash'],
    userDuck,
    userLocationZip,
    vehicleCondition,
    vehiclePaymentDetails = {},
    ...rest
  } = _ref;
  const {
    dealerId = null,
    // mmd ID
    dealerZip = null,
    defaultFinanceTerms: customDefaultFinanceTerm = null,
    digitalRetailingType,
    disableIncentives = false,
    disableTrade = false,
    enableCreditApp: enableCreditAppLink = false,
    enableCreditVerification = false,
    enableExternalPaymentFetch,
    enableOfferUpgrades,
    enablePayoffQuote = false,
    enablePriceBreakdown = false,
    enableTaxesAndFees = false,
    enableWalletByDefault = false,
    hideCTAs = false,
    hideSendOfferCTA = false,
    includeTaxesAndFees: applyTaxesAndFees = false,
    interestRates: nationalRates = {},
    isSingleColumn,
    partnerId,
    paymentCalculationFetcher = {},
    paymentCalculationUrl,
    paymentIncentivesFetcher = {},
    selectedCriteria = {},
    shopperZip = null,
    showLenderName,
    showLendingOptions = true,
    showMsrp,
    showNationalAvg = true,
    walletZip = null
  } = useContext(ConfigContext) ?? {};
  const zipCodeValue = walletZip || userLocationZip || shopperZip || dealerZip;
  const zip = zipCodeValue ? zipCodeValue.toString() : null;
  /**
   *  'enableCreditAppCTA' is controlled by brand config in FYC and will be true for Accelerate and SACA credit app's
   *  'enableCreditAppLink' is controlled by tenant settings in syndication and will be true for Accelerate tenant.
   */
  const enableCreditApp = enableCreditAppCTA || enableCreditAppLink;

  /**
   * 'estimatedPayment' is controlled by DDC's payment payload and is used for UDS Ecomm tenant.
   * */
  const isPennyPerfectPayment = vehiclePaymentDetails.estimatedPayment === false;
  const {
    // TODO move this to a config value. FYC can set based on brand config.
    nds_calc: [, {
      trade_in_link: enableTradeIn
    }]
  } = useFeatures(['nds_calc']);
  const currentVehiclePaymentDetails = useRef(vehiclePaymentDetails);
  const isLeaseEnabled = supportedDealTypes.includes('lease');
  const isCashEnabled = supportedDealTypes.includes('cash');
  const [showLendingOptionsModal, setShowLendingOptionsModal] = useState(false);
  const [lendingOptions, setLendingOptions] = useState([]);

  // Handler to trigger Lending Options Modal open
  const handleLendingOptionsCTA = event => {
    setShowLendingOptionsModal(true);
    sendClick(ndsLendingOptionsCTAClick, event, {
      listingId,
      enableCreditApp
    });
  };
  const {
    paymentsEnabled = true
  } = personalPaymentProps;
  const {
    dealType,
    creditType,
    budget,
    monthlyBudget,
    tradeIn = {},
    interactions: walletInteractions = {},
    walletId,
    loanTerm: savedLoanTerm = 72
  } = paymentsInfo;
  const {
    hasUpdatedCreditOrTerm = false,
    hasRequestedTaxesFees = false,
    paymentEmphasis = false,
    hasOpenedWallet = false,
    hasWalletInteractionWithinVDP = false,
    isNationalRates = false,
    autoFilledListing = []
  } = walletInteractions;
  const isPenciledVdpListing = useSelector(state => paymentsDuck.selectors.isPenciledVdpListing(state, listingId)) || false;
  const {
    ico: [icoTradeIn] = [],
    source = ''
  } = tradeIn;
  const {
    paymentServices = {},
    clickType
  } = listing;
  const {
    incentivesUrl = {}
  } = paymentServices;
  const {
    selectedLoanTerm: loanTerm,
    selectedCreditType: creditTypeValue,
    leaseDownPayment: defaultLeaseDownPayment,
    financeDownPayment: downPaymentValue,
    financeDownPaymentPercentage: downPaymentPercentValue = null,
    interestRate,
    selectedLeaseTerm: leaseTerm,
    selectedLeaseMileage: leaseMileageRange,
    leaseMileageOptions,
    leaseTermOptions,
    tradeInValue = 0,
    amountOwed = 0,
    creditLowScore
  } = selectedCriteria;

  // Set up tab-based pricing, initialized to an empty object until Payment Services responses are available
  const [cashPricingDetails, setCashPricingDetails] = useState({});
  const [financePricingDetails, setFinancePricingDetails] = useState({});
  const [leasePricingDetails, setLeasePricingDetails] = useState({});

  // set initial state local values
  const [icoTradeInVehicle, setIcoTradeVehicle] = useState(icoTradeIn);
  const [localTradeInValue, setLocalTradeInValue] = useState(tradeInValue);
  const [localAmountOwed, setLocalAmountOwed] = useState(amountOwed);
  const [offerPrice, setOfferPrice] = useState();
  const [showOfferPrice, setShowOfferPrice] = useState(false);
  const [isNDSPenciled, setIsNDSPenciled] = useState(false);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [pencilingEventData, setPencilingEventData] = useState({});
  const selectedInterestRate = typeof interestRate?.[vehicleCondition] === 'number' && interestRate?.[vehicleCondition] >= 0 ? interestRate?.[vehicleCondition] : 4.5;
  const [activeTab, setActiveTab] = useState();
  const [selectedLoanTerm, setSelectedLoanTerm] = useState(loanTerm);
  const [localInterestRate, setLocalInterestRate] = useState(selectedInterestRate);
  const [financeDownPayment, setFinanceDownPayment] = useState(downPaymentValue);
  const [financeDownPaymentPercentageValue, setFinanceDownPaymentPercentageValue] = useState(downPaymentPercentValue);
  const [selectedCreditTier, setSelectedCreditTier] = useState(creditTypeValue);
  const [selectedLeaseTerm, setSelectedLeaseTerm] = useState(leaseTerm);
  const [selectedMileage, setSelectedMileage] = useState(leaseMileageRange);
  const customRate = paymentsInfo?.interestRate?.custom ?? null;
  const [useCustomRate, setUseCustomRate] = useState(typeof customRate === 'number');
  const [financeOffersObj, setFinanceOffersObj] = useState({});
  const [leaseOffersObj, setLeaseOffersObj] = useState({});
  const [cashOffersObj, setCashOffersObj] = useState({});
  const [hasTaxesFeesModalOpen, setHasTaxesFeesModalOpen] = useState(false);
  const [includeTaxesAndFees, setIncludeTaxesAndFees] = useState(hasRequestedTaxesFees || applyTaxesAndFees);
  const [taxesAndFeesObj, setTaxesAndFeesObj] = useState({});
  const [paymentCalculationDetails, setPaymentCalculationDetails] = useState(pencilingPaymentDetails);
  const [previousCompletedInput, setPreviousCompletedInput] = useState('');
  const [isSupportingLinkClicked, setIsSupportingLinkClicked] = useState(false);
  useEffect(() => {
    if (!activeTab) {
      // calls on page load or VDP TO VDP transition
      setActiveTab(initialDealType);
      updatePaymentsInfo({
        dealType: initialDealType,
        zip
      });
      updateMyWalletInteractions({
        hasWalletInteractionWithinVDP: false,
        ...(enableWalletByDefault && {
          paymentEmphasis: true
        })
      });
    } else if (dealType && dealType !== activeTab) {
      // calls only when dealType get updated via sdp
      setActiveTab(dealType);
      setPencilingEventData(prevData => ({
        ...prevData,
        dealType
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealType]);
  const {
    originalSalePrice,
    salePrice,
    msrp,
    salePriceBasedOnMSRP = false
  } = {
    cash: !_isEmpty(cashPricingDetails) && cashPricingDetails,
    lease: !_isEmpty(leasePricingDetails) && leasePricingDetails,
    finance: !_isEmpty(financePricingDetails) && financePricingDetails
  }[activeTab] || {};
  const isDRAccelerate = digitalRetailingType === 'accelerate';
  const isLeaseDeal = activeTab === 'lease';
  const isFinanceDeal = activeTab === 'finance';
  const icoTradeInOffer = icoTradeInVehicle?.instantCashOffer ?? false;
  let price;
  if (['lease', 'cash'].includes(activeTab)) {
    price = salePriceBasedOnMSRP ? msrp : originalSalePrice;
  }
  if (!price) {
    price = originalSalePrice || salePrice || msrp || fallbackListingPrice;
  }
  const [localPrice, setLocalPrice] = useState(price);
  const walletDownPaymentFallBack = calculateDownPayment(localPrice, {
    downPayment: financeDownPaymentPercentageValue === null ? financeDownPayment : null,
    downPaymentPercent: financeDownPaymentPercentageValue
  });
  const [leaseDownPayment, setLeaseDownPayment] = useState(paymentEmphasis ? walletDownPaymentFallBack : defaultLeaseDownPayment);
  const leaseDownPaymentFallBack = calculateDownPayment(localPrice, {
    downPayment: leaseDownPayment || null,
    downPaymentPercent: 0.1
  });
  const ndsPencilingFeature = isDRAccelerate ? 'dr3' : 'MyWallet';
  const obfuscatedCredit = getObfuscatedCredit(selectedCreditTier);

  // Only used for firing UDS pencilling data of limited set.
  useEffect(() => {
    const pencilingData = {
      offerPrice: offerPrice ?? price,
      downPayment: downPaymentValue ?? walletDownPaymentFallBack,
      creditTier: obfuscatedCredit,
      term: dealType === 'lease' ? leaseTerm : loanTerm,
      dealType: activeTab ?? initialDealType
    };
    eventBus.emit(UDS_PENCILING_DATA_UPDATED, pencilingData);
  }, [dealType, downPaymentValue, leaseTerm, loanTerm, obfuscatedCredit, offerPrice,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(pencilingEventData), price, walletDownPaymentFallBack, activeTab, initialDealType]);

  // Specifically used for firing UDS pencilling data in syndicated version of esntial VDP integrated with external payments handled via event communication.
  useEffect(() => {
    const pencilingData = {
      dealType: activeTab || initialDealType,
      downPayment: financeDownPayment,
      creditTier: obfuscatedCredit,
      leaseTerm: selectedLeaseTerm,
      loanTerm: selectedLoanTerm,
      tradeInValue: localTradeInValue,
      amountOwed: localAmountOwed,
      userZip: zip,
      mileage: selectedMileage,
      downPaymentPercent: financeDownPaymentPercentageValue
    };
    if (enableExternalPaymentFetch && onFetchPersonalizedPaymentDetails && (enableExernalFetchForVehicle || isNDSPenciled)) {
      updateIsLoading({
        isLoading: true
      }); // To handle syndication esntial payment loading state
      onFetchPersonalizedPaymentDetails(pencilingData);
    }

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [selectedCreditTier, selectedLeaseTerm, selectedLoanTerm, financeDownPaymentPercentageValue !== null ? financeDownPaymentPercentageValue : financeDownPayment,
  // Finance Down payment has dependency with Down payment percentage
  financeDownPayment >= 0 ? null : leaseDownPayment,
  // Lease Down payment has dependency with Finance Down payment
  localTradeInValue, localAmountOwed, financeDownPayment >= 0 ? null : financeDownPaymentPercentageValue,
  // Down payment percentage has dependency with Finance Down payment
  enableExernalFetchForVehicle]);
  const handleNDSClick = useCallback(async function (event) {
    let userModified = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    if (!isNDSPenciled) {
      setIsNDSPenciled(true);
      const consumerMetadata = await getConsumerMetadata();
      consumerMetadata.walletId = paymentsInfo?.walletId;
      sendClick(ndsCalcInteraction, event, {
        listingId,
        feature: ndsPencilingFeature,
        consumerMetadata,
        walletData: {
          paymentsInfo,
          userModified,
          price,
          offerPrice
        }
      });
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isNDSPenciled, listingId, ndsPencilingFeature, sendClick, paymentsInfo?.walletId, price, offerPrice]);

  /**
   * Handles tab selection change from user click interaction. Sets the new active tab, updates wallet payment info,
   * and sends analytics events.
   *
   * @param {('finance'|'lease'|'cash')} key - The key of the tab to select
   * @param {SyntheticEvent} event - The event that triggered the tab selection
   */
  const onTabSelect = function (event) {
    let key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'finance';
    setActiveTab(key);
    updatePaymentsInfo({
      ...paymentsInfo,
      dealType: key,
      interactions: {
        ...paymentsInfo.interactions,
        ...(!walletInteractions?.paymentEmphasis && {
          paymentEmphasis: true
        })
      }
    });
    const userModified = 'DEAL_TAB_CHANGED';
    sendClick(ndsNavigationClick, event, {
      cmp: `${key}-tab`,
      eventResult: `${key}-tab`,
      listingId,
      walletData: {
        paymentsInfo,
        userModified,
        price,
        offerPrice
      }
    });
    handleNDSClick(event, userModified);
  };
  const handleFallbackPayment = useCallback(fallbackPayment => {
    updatePaymentPencils({
      fallbackPayment
    });
  }, [updatePaymentPencils]);
  const handleCreateWalletId = () => {
    if (!walletId && !paymentsInfo?.walletId) {
      debugWallet('UDS', 'create wallet ID request from UDS');
      eventBus.emit(WALLET_CREATE_ID_REQUEST);
    }
  };
  const handleNDSPencilling = async (event, eventData) => {
    handleCreateWalletId();
    const {
      cmp,
      eventResult,
      eventName,
      feature,
      userModified = ''
    } = eventData;
    sendClick(ndsPencilingEvents, event, {
      listingId,
      cmp,
      eventResult,
      eventName,
      feature,
      activeTab,
      walletData: {
        paymentsInfo,
        userModified,
        price,
        offerPrice
      }
    });
  };
  const handlePaymentsFallback = function () {
    let showNationalRates = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    setLeaseOffersObj({});
    setFinanceOffersObj({});
    updateIsLoading({
      isLoading: false
    });
    updateMyWalletInteractions({
      hasLeaseError: true,
      isNationalRates: showNationalRates
    });
  };

  // destructure lease object when present
  const {
    payment: leasePayment,
    downPayment: leaseDownPaymentValue,
    capCost: {
      adjustedCapCost,
      capitalizedTaxesAndFees
    } = {},
    dueInAdvance: {
      firstPayment,
      cashDueFromCustomer,
      diaTaxes,
      diaDealerFees,
      diaGovFees,
      diaLenderFees
    } = {}
  } = leaseOffersObj;
  let totalEquity = 0;
  let totalTax = 0;
  if (!disableTrade) {
    totalEquity = localTradeInValue - localAmountOwed;
    if (icoTradeInOffer) {
      totalEquity = icoTradeInOffer - localAmountOwed;
    }
  }
  if (enableTaxesAndFees) {
    totalTax = Math.floor(({
      lease: leaseOffersObj,
      cash: cashOffersObj
    }[activeTab] ?? financeOffersObj).taxesFees?.totalTaxesFees || 0);
  }
  const totalCash = cashOffersObj?.pricingDetails?.salePrice || cashOffersObj?.pricingDetails?.msrp || localPrice;
  const totalTaxOnCash = enableTaxesAndFees && cashOffersObj?.taxesFees?.totalTaxesFees ? cashOffersObj?.taxesFees?.totalTaxesFees : 0;
  const totalCashOut = totalCash - totalEquity + totalTaxOnCash;
  let totalFinancing = localPrice - totalEquity - financeDownPayment + totalTax;
  let dueAtSigningValue;
  if (isLeaseEnabled && isLeaseDeal) {
    totalFinancing = adjustedCapCost || 0;
    dueAtSigningValue = leaseDownPaymentValue;
    if (typeof leaseDownPayment === 'number' && leaseDownPayment >= 0) {
      dueAtSigningValue = leaseDownPayment;
    }
  }
  const formattedTotalPrice = formatNumber({
    value: localPrice
  });
  const formattedTotalTax = formatNumber({
    value: totalTax
  });
  const isFinancingUnavailable = totalFinancing < 0;
  const isFinanceErrorState = isFinancingUnavailable || !showNationalAvg && (Object.keys(financeOffersObj).length === 0 || financeOffersObj.payment === undefined && financeOffersObj.pricing || financeOffersObj?.lender === undefined || financeOffersObj?.lender === 'National Average Rate');
  const isLeaseErrorState = Object.keys(leaseOffersObj).length === 0 || leaseOffersObj.payment === undefined && leaseOffersObj.pricingDetails;

  // only tell the components there is a payment error when it's related to the active tab
  const paymentErrorState = isFinanceDeal && isFinanceErrorState ||
  // Handles finance error state
  isLeaseDeal && isLeaseErrorState && !isLoading; // Handles lease error state

  // Used only to prepopulate UDS with existing deal where receipt is hidden which possible only for Non DR dealers.
  useEffect(() => {
    if (showNdsWithDeal && !_isEmpty(dealXgData)) {
      const {
        deal,
        tradeIn: dealXgTradeIn
      } = dealXgData;
      const {
        creditRating,
        dealType: dealXgDealType,
        downPayment: dealXgDownPayment,
        leaseMileageRange: dealXgLeaseMileageRange,
        leaseTerm: dealXgLeaseTerm,
        loanTerm: dealXgLoanTerm,
        rate
      } = deal;
      const {
        tradeSubmitFlag,
        totalAllowance,
        totalPayoff,
        netTrade
      } = dealXgTradeIn;
      const dealTypeWithFallback = activeTab ?? dealXgDealType.toLowerCase();
      const updatedPaymentsInfo = {
        ...paymentsInfo,
        creditType: creditRating ?? creditTypeValue,
        dealType: dealTypeWithFallback,
        downPayment: dealXgDownPayment ?? downPaymentValue,
        interestRate: {
          ...interestRate,
          used: rate ?? localInterestRate
        },
        loanTerm: dealXgLoanTerm ?? loanTerm,
        tradeIn: {
          ...tradeIn,
          netTrade,
          totalAllowance,
          totalPayoff,
          tradeSubmitFlag
        },
        interactions: {
          ...paymentsInfo.interactions,
          fromNds: true
        }
      };
      if (dealTypeWithFallback === 'Lease') {
        updatedPaymentsInfo.leaseMileageRange = dealXgLeaseMileageRange ?? leaseMileageRange;
        updatedPaymentsInfo.leaseTerm = dealXgLeaseTerm ?? leaseTerm;
      }
      updatePaymentsInfo(updatedPaymentsInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNdsWithDeal]);

  // Used to update all local state
  useEffect(() => {
    setLocalAmountOwed(amountOwed);
    setFinanceDownPayment(downPaymentValue ?? walletDownPaymentFallBack);
    setFinanceDownPaymentPercentageValue(downPaymentPercentValue);
    setLocalTradeInValue(tradeInValue);
    setSelectedCreditTier(creditTypeValue);
    setSelectedLoanTerm(loanTerm);
    setSelectedLeaseTerm(leaseTerm);
    setSelectedMileage(leaseMileageRange);
    if (icoTradeIn) {
      // Checking for icoTradeIn so that ico checkbox is visible all time even on user entered tradeIn.
      setIcoTradeVehicle(icoTradeIn);
    }
    if (downPaymentValue && isLeaseEnabled) {
      // Apply downPaymentValue to leaseDownPayment
      setLeaseDownPayment(downPaymentValue);
    }
    if (!showOfferPrice) {
      setLocalPrice(price);
    }
  }, [amountOwed, downPaymentValue, tradeInValue, creditTypeValue, loanTerm, leaseTerm, leaseMileageRange, icoTradeIn, downPaymentPercentValue, isLeaseEnabled, price, showOfferPrice]);

  // For UDS + Esntial tenant
  const buildTaxesFeesObject = taxesFees => {
    let taxTotal = 0;
    let feeTotal = 0;
    if (taxesFees.taxes) {
      taxesFees.taxes.forEach(taxItem => {
        taxTotal += taxItem.amount;
      });
    }
    const refinedTaxesFees = {
      showTaxTotalOnly: true,
      totalTaxes: Math.floor(taxTotal),
      totalTaxesFees: taxesFees.taxesAndFees ?? 0
    };
    if (taxesFees.fees) {
      taxesFees.fees.forEach(item => {
        refinedTaxesFees[item.name] = item.amount;
      });
      taxesFees.fees.forEach(fee => {
        feeTotal += fee.amount;
      });
    }
    if (!taxesFees.totalTaxesFees) {
      refinedTaxesFees.totalTaxesFees = Math.floor(feeTotal + taxTotal);
    }
    return refinedTaxesFees;
  };

  // apply payments data from payment calculation service to calculator.
  useEffect(() => {
    if (!_isEmpty(paymentCalculationDetails) && selectedCreditTier && (selectedLoanTerm || selectedLeaseTerm)) {
      const financeOfferValues = getLowestPersonalizedPayment(paymentCalculationDetails, obfuscatedCredit, selectedLoanTerm);
      const leaseOfferValues = getLeaseOffer(paymentCalculationDetails, obfuscatedCredit, selectedLeaseTerm);
      const cashOfferValues = getCashOffer(paymentCalculationDetails, obfuscatedCredit);
      const isNationalRatesValue = !_isEmpty(paymentCalculationDetails?.rates?.[obfuscatedCredit]?.nationalOffers?.[selectedLoanTerm]?.[0]);
      if (hasTaxesFeesModalOpen || !isNationalRatesValue) {
        if (!_isEmpty(vehiclePaymentDetails) && enableExternalPaymentFetch) {
          setTaxesAndFeesObj({
            finance: financeOfferValues?.taxes || financeOfferValues?.fees ? buildTaxesFeesObject(financeOfferValues) : {},
            lease: leaseOfferValues?.taxes || leaseOfferValues?.fees ? buildTaxesFeesObject(leaseOfferValues) : {},
            cash: cashOfferValues?.taxes || cashOfferValues?.fees ? buildTaxesFeesObject(cashOfferValues) : {}
          });
        } else {
          setTaxesAndFeesObj({
            finance: financeOfferValues.taxesFees,
            lease: {
              capCost: leaseOfferValues.capCost,
              dueInAdvance: leaseOfferValues.dueInAdvance
            },
            cash: cashOfferValues.taxesFees
          });
        }
        updateMyWalletInteractions({
          isNationalRates: false
        });
      } else if (isNationalRatesValue && showNationalAvg) {
        updateMyWalletInteractions({
          isNationalRates: isNationalRatesValue
        });
      }
      if (paymentCalculationDetails.apply) {
        // apply finance data when available
        if (!_isEmpty(financeOfferValues)) {
          const {
            payment,
            offeredBy,
            pricingDetails = {},
            incentiveIds,
            taxesFees,
            totalOfPayments = 0,
            specialApr,
            totalInterest = null
          } = financeOfferValues;
          setFinanceOffersObj({
            payment,
            lender: offeredBy,
            interestRate: financeOfferValues[vehicleCondition],
            manufacturerOffer: pricingDetails?.rebate,
            appliedIncentiveIds: incentiveIds,
            pricing: pricingDetails,
            showSpecialAPR: specialApr,
            downPayment: financeOfferValues.downPayment,
            totalOfPayments,
            totalInterest,
            taxesFees: financeOfferValues.taxesAndFees ? {
              ...taxesFees,
              totalTaxesFees: financeOfferValues.taxesAndFees // use preferred field for syndicated implementation
            } : taxesFees
          });
        }

        // apply lease data when available and lease is turned on
        if (isLeaseEnabled && !_isEmpty(leaseOfferValues)) {
          setLeaseOffersObj({
            ...leaseOfferValues,
            appliedIncentiveIds: leaseOfferValues?.incentiveIds ?? [],
            taxesFees: leaseOfferValues.taxesAndFees ? {
              ...leaseOfferValues.taxesFees,
              totalTaxesFees: leaseOfferValues.taxesAndFees // use preferred field for syndicated implementation
            } : leaseOfferValues.taxesFees
          });
          updateListingLeasePayments({
            [listingId]: {
              ...leaseOfferValues
            }
          });
          updateMyWalletInteractions({
            hasLeaseError: false
          });
        } else if (isLeaseEnabled) {
          updateMyWalletInteractions({
            hasLeaseError: true
          });
          setLeaseOffersObj({});
        }

        // apply cash data when available
        if (isCashEnabled && !_isEmpty(cashOfferValues)) {
          setCashOffersObj({
            ...cashOfferValues,
            appliedIncentiveIds: cashOfferValues?.incentiveIds ?? [],
            taxesFees: cashOfferValues.taxesAndFees ? {
              ...cashOfferValues.taxesFees,
              totalTaxesFees: cashOfferValues.taxesAndFees // use preferred field for syndicated implementation
            } : cashOfferValues.taxesFees
          });
          updateListingCashPayments({
            [listingId]: {
              ...cashOfferValues
            }
          });
        } else {
          setCashOffersObj({});
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCalculationDetails]);

  // fetch payments data from payment calculation service.
  const fetchPersonalizedPayment = async function () {
    let withTaxesAndFees = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    let applyResults = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    let userZip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : zip;
    if (Object.keys(paymentCalculationFetcher).length > 0 && paymentCalculationUrl && userZip) {
      updateIsLoading({
        isLoading: true
      });
      let leaseParams = {};
      if (isLeaseEnabled) {
        leaseParams = {
          leaseTerm: selectedLeaseTerm,
          milesPerYear: selectedMileage,
          leaseDownPayment: paymentEmphasis ? downPaymentValue || walletDownPaymentFallBack : leaseDownPaymentFallBack
        };
      }
      const _paymentsInfo = {
        loanTerm: selectedLoanTerm,
        creditType: obfuscatedCredit,
        downPayment: downPaymentValue,
        downPaymentPercent: financeDownPaymentPercentageValue,
        ...(!disableTrade && {
          tradeIn: {
            value: icoTradeInOffer || localTradeInValue,
            amountOwed: localAmountOwed,
            condition: vehicleCondition
          }
        }),
        ...leaseParams,
        includeCash: true,
        includeTaxesFees: enableTaxesAndFees ? withTaxesAndFees : false
      };
      const personalizedPaymentParams = getPersonalizedPaymentParams({
        paymentsInfo: _paymentsInfo,
        userZip,
        offerPrice
      });
      const detailRates = (await paymentCalculationFetcher.fetch(paymentCalculationUrl + `&${qs.stringify({
        ...personalizedPaymentParams,
        // Pass low credit score only when user select 'rebuilding' aka 'poor' credit
        ...(creditTypeValue === 'rebuilding' && creditLowScore && {
          creditLowScore
        })
      })}`)) || {};
      if (detailRates?.rates) {
        const paymentResponse = {
          rate: detailRates.rates,
          dealType: activeTab ?? initialDealType,
          creditType: selectedCreditTier,
          loanTerm: selectedLoanTerm,
          leaseTerm: selectedLeaseTerm,
          tradeInValue: localTradeInValue,
          amountOwed: localAmountOwed,
          customRate: useCustomRate ? customRate : null,
          paymentErrorState
        };
        debugWallet('UDS', 'PaymentCalcResponse:Update', paymentResponse);

        // Send payment calculation response and calculator payment details
        eventBus.emit(UDS_PAYMENT_CALC_RESPONSE_UPDATE, paymentResponse);
        updateIsLoading({
          isLoading: false
        });
        const calculationDetails = {
          ...detailRates,
          apply: applyResults
        };
        setPaymentCalculationDetails(calculationDetails);
        if (listingId && updatePencilingPayments) {
          updatePencilingPayments({
            [listingId]: calculationDetails
          });
        }
      } else if (Object.keys(detailRates).length === 0 || detailRates && detailRates?.error) {
        // eslint-disable-next-line no-console
        console.error('Error in fetching Payment Calc response:', detailRates?.error?.message);
        handlePaymentsFallback(showNationalAvg);
        updateIsLoading({
          isLoading: false
        });
      }
    } else {
      handlePaymentsFallback(showNationalAvg);
    }
  };
  const consumePersonalizedPayment = useCallback(updatePayment => {
    if (updatePayment && enableExternalPaymentFetch) {
      setPaymentCalculationDetails({
        ...vehiclePaymentDetails,
        apply: !hasTaxesFeesModalOpen
      });
      currentVehiclePaymentDetails.current = vehiclePaymentDetails;
      updateIsLoading({
        isLoading: false
      });
    } else if (!updatePayment && isLoading && enableExternalPaymentFetch && !_isEqual(currentVehiclePaymentDetails.current, vehiclePaymentDetails)) {
      handlePaymentsFallback(showNationalAvg);
    }
  }, [vehiclePaymentDetails]);
  const extractVin = url => {
    const match = url.match(/vin=([A-Za-z0-9]+)/);
    if (match) {
      return match[1];
    }
    return null;
  };
  useEffect(() => {
    if (paymentsEnabled && !enableExternalPaymentFetch && paymentCalculationUrl) {
      // vin comparison to prevent a race condition in which a duplicate Payment Calc call is made
      // with a previous VDP's paymentCalculationUrl upon VDP to VDP navigation due to a delayed config update
      const vinFromUrl = extractVin(paymentCalculationUrl);
      if (vinFromUrl === listingVin) {
        fetchPersonalizedPayment(includeTaxesAndFees);
      } else {
        // eslint-disable-next-line no-console
        console.log('VIN mismatch between listing and paymentCalculationUrl');
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
  // The below dependency array determines when to make a call to payments if any of the value changes
  customRate, financeDownPayment >= 0 ? null : financeDownPaymentPercentageValue,
  // Down payment percentage has dependency with Finance Down payment
  financeDownPayment >= 0 ? null : leaseDownPayment,
  // Lease Down payment has dependency with Finance Down payment
  financeDownPaymentPercentageValue !== null ? financeDownPaymentPercentageValue : financeDownPayment,
  // Finance Down payment has dependency with Down payment percentage
  icoTradeInOffer, isLeaseEnabled, listingId, localAmountOwed, localTradeInValue, offerPrice, paymentCalculationUrl, paymentsEnabled, selectedCreditTier, selectedLeaseTerm, selectedLoanTerm, selectedMileage, useCustomRate, vehicleCondition]); // If any changes to above array then we need test the api calls before and after wallet in enabled were it has to be only one api call.

  useVehiclePaymentDetails(vehiclePaymentDetails, consumePersonalizedPayment, obfuscatedCredit, selectedLoanTerm, selectedLeaseTerm, vehicleCondition, showLenderName, activeTab, hasTaxesFeesModalOpen);
  useEffect(() => {
    setFinancePricingDetails(financeOffersObj?.pricing ?? {});
    setFinanceDownPayment(financeOffersObj?.downPayment ?? walletDownPaymentFallBack);
    // Interest rate update
    if (typeof financeOffersObj.interestRate === 'number' && financeOffersObj.interestRate >= 0 && typeof customRate !== 'number') {
      setLocalInterestRate(financeOffersObj.interestRate);
      setUseCustomRate(false);
    }

    // Lease
    if (isLeaseEnabled) {
      // No need of uncessary state updates for NO lease
      setLeasePricingDetails(leaseOffersObj?.pricingDetails ?? {});
      setLeaseDownPayment(paymentEmphasis ? financeOffersObj?.downPayment || walletDownPaymentFallBack : leaseOffersObj?.downPayment || leaseDownPaymentFallBack);
    }

    // Cash
    setCashPricingDetails(cashOffersObj?.pricingDetails ?? {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeOffersObj, leaseOffersObj, cashOffersObj, isLeaseEnabled, price]);

  // apply custom interest
  useEffect(() => {
    if (typeof customRate === 'number') {
      setLocalInterestRate(customRate);
      setUseCustomRate(true);
    }
  }, [customRate]);
  const prevAutoFillData = autoFilledListing.length === 0 ? {} : {
    ...autoFilledListing[listingId]
  };
  useEffect(() => {
    const currentAutoFillData = {
      creditType,
      loanTerm: loanTerm?.toString(),
      leaseTerm: leaseTerm?.toString(),
      budget,
      monthlyBudget,
      tradeInValue,
      amountOwed,
      downPaymentPercentValue,
      downPaymentValue,
      dueAtSigningValue
    };
    const hasAutoFillData = amountOwed || tradeInValue || financeDownPayment || dueAtSigningValue;
    const matchAutofillData = _isEqual(currentAutoFillData, prevAutoFillData);
    if (!isLoading && hasOpenedWallet && hasAutoFillData && !matchAutofillData && !isAutoFilled && !hasWalletInteractionWithinVDP) {
      sendClick(ndsAutoFill, null, {
        listingId,
        walletData: {
          paymentsInfo,
          price,
          offerPrice
        }
      });
      setIsAutoFilled(true);
      updateMyWalletInteractions({
        autoFilledListing: {
          ...autoFilledListing,
          [listingId]: currentAutoFillData
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendClick, listingId, isLoading, amountOwed, tradeInValue, financeDownPayment, dueAtSigningValue, isAutoFilled]);
  const analyticsData = {
    isNDSPenciled,
    listingId,
    price,
    offerPrice
  };
  useExternalDealTypeChange(supportedDealTypes, setActiveTab, updatePaymentsInfo, paymentsDuck, analyticsData, handleNDSClick);

  // Listen for external incentives request if we can fetch them
  const incentivesParams = {
    dealerId,
    partnerId,
    listingType: listing?.type,
    vin: listingVin ?? listing?.vin,
    zip
  };
  useExternalIncentivesRequest(activeTab, incentivesParams, paymentIncentivesFetcher);
  const creditTierOptions = getCreditTierOptions(nationalRates);
  const updateUserPaymentInfo = useCallback(function (key, value) {
    let updatedZip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    let hasCreditVerified = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    const isCreditType = key === 'creditType';
    const isLoanTerm = key === 'loanTerm';
    const creditTierRates = isCreditType ? value : selectedCreditTier;
    const derivedCreditTierRates = nationalRates[ratesLabels[creditTierRates]?.decodedValue];
    const termRates = isLoanTerm ? value : selectedLoanTerm;
    const derivedTermRates = derivedCreditTierRates.rates[termRates];
    const derivedCreditType = isCreditType ? value : obfuscatedCredit;
    const updatesCreditOrTerm = isCreditType || isLoanTerm;
    const rateObject = useCustomRate ? {
      custom: customRate,
      ...derivedTermRates
    } : {
      ...derivedTermRates
    };
    const updatedCreditType = isCreditType ? ratesLabels[value]?.decodedValue : selectedCreditTier;
    const updatedLoanTerm = isLoanTerm ? value : selectedLoanTerm;

    // check to ensure previousSelectedLoanTerm is not a custom value
    const previousSelectedLoanTerm = isLoanTermCustom(savedLoanTerm, updatedCreditType) ? 72 : savedLoanTerm;
    const fallBackLoanTerm = isLoanTermCustom(updatedLoanTerm, updatedCreditType) ? previousSelectedLoanTerm : null;
    let updatedTradeIn = {};
    if (key === 'tradeInInput') {
      updatedTradeIn = {
        source: 'USER',
        amountOwed: tradeIn.amountOwed,
        value
      };
    } else if (key === 'amountOwedInput') {
      updatedTradeIn = {
        ...tradeIn,
        amountOwed: value
      };
    }
    const paymentObject = {
      /* When wallet is not enabled and UDS is pencilled first time override wallet with UDS selections */
      ...(!paymentEmphasis && buildPayments(selectedCriteria, obfuscatedCredit)),
      creditType: derivedCreditType,
      ...(key === 'downPayment' && {
        downPaymentPercent: null
      }),
      // downPaymentPercent null when there is a down payment pencil
      ...(!isCreditType && {
        [key]: value
      }),
      ...(Object.keys(updatedTradeIn).length > 0 && {
        tradeIn: updatedTradeIn
      }),
      // fall back to 72 term if user changes credit tier after loan term selection of 84 which applies only to excellent tier
      ...(Number(selectedLoanTerm) === 84 && isCreditType && value !== 'excellent' && customDefaultFinanceTerm !== 84 && {
        loanTerm: 72
      }),
      fallBackLoanTerm,
      interestRate: rateObject,
      interactions: {
        ...paymentsInfo.interactions,
        isNationalRates: false,
        // resetting national rates.
        hasUpdatedNDSCalc: true,
        hasUpdatedCreditOrTerm: hasUpdatedCreditOrTerm || updatesCreditOrTerm,
        paymentEmphasis: true,
        disablePaymentsPopover: true,
        ...(key === 'hasRequestedTaxesFees' && {
          hasRequestedTaxesFees: true
        }),
        fromNds: true
      },
      ...(updatedZip && {
        zip: updatedZip
      }),
      ...(hasCreditVerified && {
        creditVerification: {
          verified: hasCreditVerified,
          verifiedDate: new Date().getTime()
        }
      })
    };
    if ((isCreditType || isLoanTerm) && Object.keys(rateObject).length !== 0) {
      setLocalInterestRate(useCustomRate ? rateObject.custom : rateObject[vehicleCondition]);
    }
    updatePaymentsInfo(paymentObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nationalRates, selectedCreditTier, selectedLoanTerm, useCustomRate, customRate, updatePaymentsInfo, paymentsInfo]);
  const handleSelectChange = event => {
    const {
      target
    } = event;
    let cmp = '';
    let eventResult = '';
    let eventName;
    let userModified = '';
    if (target.name === 'creditTierInput') {
      userModified = 'CREDIT_TIER_CHANGED';
      const splitLabels = nationalRates[target.value].label.split(' (');
      const mappedLabels = [];
      splitLabels.forEach((v, i) => {
        if (typeof v !== 'undefined') {
          if (i === 0) {
            mappedLabels.push(`${v}(`);
          } else {
            mappedLabels.push(v.replaceAll(/\s/g, ''));
          }
        }
      });
      const optionText = mappedLabels.join('');
      updateUserPaymentInfo('creditType', getObfuscatedCredit(target.value));
      cmp = `crd_rtg - ${optionText.toLowerCase()}`;
      eventResult = 'credit-rating';
    } else if (target.name === 'termInput') {
      userModified = 'TERM_LENGTH_CHANGED';
      updateUserPaymentInfo('loanTerm', target.value);
      cmp = `term_lmt-${target.value}`;
      eventResult = 'term';
    } else if (target.name === 'mileageInput') {
      userModified = 'MILES_INPUT_CHANGED';
      updateUserPaymentInfo('leaseMileageRange', target.value);
      cmp = `nds_lease-term-${selectedLeaseTerm}-${formatNumber({
        value: target.value
      })}`;
      eventName = 'lease term';
      eventResult = 'miles-per-year';
    } else if (target.name === 'leaseTermInput') {
      userModified = 'LEASE_TERM_LENGTH_CHANGED';
      updateUserPaymentInfo('leaseTerm', target.value);
      cmp = `nsd_lease-term-${target.value}-${formattedNumber({
        value: selectedMileage
      })}`;
      eventName = 'lease term';
      eventResult = 'lease';
    }
    setPreviousCompletedInput(target.name);
    handleNDSClick(event, userModified);
    handleNDSPencilling(event, {
      cmp,
      eventResult,
      eventName,
      feature: ndsPencilingFeature,
      userModified
    });
  };
  const handleInputChange = useCallback(e => {
    e.persist();
    const {
      value,
      name
    } = e.target;
    const sanitizedValue = parseInt(value.replace(/[^\d-]/g, '') || 0, 10);
    handleCreateWalletId();
    switch (name) {
      case 'tradeInInput':
        handleNDSClick(e, 'TRADE_IN_VALUE_CHANGED');
        setPreviousCompletedInput(name);
        return updateUserPaymentInfo('tradeInInput', sanitizedValue);
      case 'amountOwedInput':
        handleNDSClick(e, 'AMOUNT_OWED_CHANGED');
        setPreviousCompletedInput(name);
        return updateUserPaymentInfo('amountOwedInput', sanitizedValue);
      case 'downPaymentInput':
        handleNDSClick(e, 'DOWN_PAYMENT_CHANGED');
        setPreviousCompletedInput(name);
        return updateUserPaymentInfo('downPayment', sanitizedValue);
      case 'dueAtSigning':
        handleNDSClick(e, 'DUE_AT_SIGNING_CHANGED');
        setPreviousCompletedInput(name);
        return updateUserPaymentInfo('downPayment', sanitizedValue);
      case 'offerPriceInput':
        handleNDSClick(e, 'OFFER_PRICE_INPUT_CHANGED');
        setLocalPrice(sanitizedValue);
        setOfferPrice(sanitizedValue);
        setShowOfferPrice(true);
        setFinanceDownPayment(calculateDownPayment(sanitizedValue, {
          downPayment: financeDownPaymentPercentageValue === null ? financeDownPayment : null,
          downPaymentPercent: financeDownPaymentPercentageValue
        }));
        setPreviousCompletedInput(name);
        return updatePaymentPencils({
          offerPrice: sanitizedValue
        });
      default:
        handleNDSClick(e);
        return null;
    }
  }, [financeDownPaymentPercentageValue, financeDownPayment, handleNDSClick, updateUserPaymentInfo]);
  const handleOnBlur = useCallback(event => {
    const {
      name
    } = event.target;
    let cmp = '';
    let eventResult = '';
    switch (name) {
      case 'tradeInInput':
        cmp = 'estm_trd_val';
        eventResult = 'trade-in';
        break;
      case 'amountOwedInput':
        cmp = 'amt_owd_trd';
        eventResult = 'amount-owned';
        break;
      case 'downPaymentInput':
        cmp = 'dwn_pymt';
        eventResult = 'down-payment';
        break;
      case 'offerPriceInput':
        cmp = 'my_ofr';
        eventResult = 'my-offer';
        break;
      case 'dueAtSigning':
        cmp = 'due_sgn';
        eventResult = 'due-at-signing';
        break;
    }
    if (event?.target?.value) {
      handleNDSPencilling(event, {
        cmp,
        eventResult,
        feature: ndsPencilingFeature
      });
    }
  }, [ndsPencilingFeature]);
  const handleSupportingLinkClick = value => setIsSupportingLinkClicked(value);
  const handleTradeInLinkClick = useCallback(event => {
    handleNDSClick(event, 'GET_TRADE_IN_VALUE_LINK_CLICKED');
    const kbbTradePanelInfo = {
      selectedPanelName: 'kbbValuation',
      hideBackButton: true
    };
    if (onFetchPersonalizedPaymentDetails) {
      // Syndication
      eventBus.emit(OPEN_WALLET, kbbTradePanelInfo);
    } else {
      // TODO: Remove this and in AT/KBB add the listener to support OPEN_WALLET event
      updateMyWallet({
        displayModal: true,
        ...kbbTradePanelInfo
      });
    }
    sendClick(ndsKBBTradeInCTAClick, event, {
      listingId
    });
  }, [handleNDSClick, listingId, sendClick, updateMyWallet]);
  const handlePayoffQuoteLinkClick = useCallback(event => {
    handleNDSClick(event, 'PAYOFF_QUOTE_UPDATED');
    updateMyWallet({
      displayModal: true,
      hideBackButton: true,
      selectedPanelName: 'payoffQuote'
    });
    sendClick(ndsPayoffQuoteCTAClick, event, {
      listingId
    });
  }, [handleNDSClick, updateMyWallet, listingId, sendClick]);
  const verifyCreditRatingInternal = useCallback(event => {
    handleSupportingLinkClick(true);
    handleNDSClick(event, 'CREDIT_VERIFICATION_CLICKED');
    updateMyWallet({
      displayModal: true,
      selectedPanelName: 'creditVerification'
    });
    sendClick(ndsVerifyCreditCTAClick, event, {
      listingId
    });
  }, [handleNDSClick, updateMyWallet, listingId, sendClick]);
  const {
    verifyCreditRatingViaEvent
  } = useExternalSelectedCriteriaUpdate(updateUserPaymentInfo, financeOffersObj, selectedLoanTerm, loanTerm, selectedCreditTier, financeDownPayment, offerPrice, price);
  const handleCreditVerificationLinkClick = event => {
    if (enableCreditVerification) {
      verifyCreditRatingViaEvent(event, 'credit-verification-cta');
    } else {
      verifyCreditRatingInternal(event);
    }
  };
  const kbbLink = () => {
    if (!enableTradeIn) {
      return null;
    }
    return /*#__PURE__*/jsxs("div", {
      className: "margin-bottom-4 display-flex",
      children: [/*#__PURE__*/jsx("div", {
        className: "margin-right-1",
        children: /*#__PURE__*/jsx(Image, {
          alt: "KBB logo",
          height: 17,
          src: "https://www.autotrader.com/content/static/img/icon/logos/kbb-logo-notext.svg",
          width: 14
        })
      }), /*#__PURE__*/jsxs(Button, {
        onClick: event => handleTradeInLinkClick(event),
        bsStyle: "text",
        children: [source === 'KBB' ? 'Update' : 'Get', " My Kelley Blue Book\xAE Value"]
      })]
    });
  };
  const payoffQuoteLink = () => {
    if (!(enableTradeIn && enablePayoffQuote)) {
      return null;
    }
    return /*#__PURE__*/jsx("div", {
      className: "margin-bottom-4",
      children: /*#__PURE__*/jsxs(Button, {
        onClick: event => handlePayoffQuoteLinkClick(event),
        bsStyle: "text",
        children: [amountOwed ? 'Update' : 'Get', " My Payoff Quote"]
      })
    });
  };
  const creditVerificationLink = /*#__PURE__*/jsx("div", {
    className: "margin-top-3",
    children: /*#__PURE__*/jsx(Button, {
      onClick: event => handleCreditVerificationLinkClick(event),
      bsStyle: "text",
      children: "Verify My Credit"
    })
  });
  const creditVerifiedIndicator = /*#__PURE__*/jsx("div", {
    className: "row",
    children: /*#__PURE__*/jsxs(Text, {
      componentClass: "div",
      color: "success",
      className: "display-flex gap-2 col-xs-12 col-lg-5 margin-top-3",
      children: [/*#__PURE__*/jsx(Glyphicon, {
        glyph: "success"
      }), /*#__PURE__*/jsx(Text, {
        size: 200,
        children: "Credit Verified"
      })]
    })
  });

  // Handler for continue callback from Lending Options Modal
  const handleLendingOptionsSelection = function () {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let event = arguments.length > 1 ? arguments[1] : undefined;
    const {
      name,
      rate,
      payment
    } = data;
    switch (name) {
      case 'Custom Rate':
        updatePaymentsInfo({
          ...paymentsInfo,
          interactions: {
            ...paymentsInfo.interactions,
            hasUpdatedNDSCalc: true,
            paymentEmphasis: true,
            fromNds: true
          },
          interestRate: {
            ...paymentsInfo?.interestRate,
            custom: Number.parseFloat(rate)
          }
        });
        break;
      case DEFAULT_RATE_LABEL:
        if (paymentsInfo?.interestRate?.custom) {
          updatePaymentsInfo({
            ...paymentsInfo,
            interestRate: {
              ...paymentsInfo?.interestRate,
              custom: null
            },
            interactions: {
              ...paymentsInfo.interactions,
              fromNds: true
            }
          });
        }
        break;
      default:
        setFinanceOffersObj({
          ...financeOffersObj,
          payment,
          lender: name,
          interestRate: Number.parseFloat(rate)
        });
        updatePaymentsInfo({
          ...paymentsInfo,
          interestRate: {
            ...paymentsInfo.interestRate,
            custom: null
          },
          interactions: {
            ...paymentsInfo.interactions,
            fromNds: true
          }
        });
        updatePaymentPencils({
          customLender: {
            payment,
            lender: name,
            rate
          }
        });
    }
    handleNDSPencilling(event, {
      cmp: 'lend_opts_cnt'
    });
  };
  const inputProps = useMemo(() => ({
    onChange: handleInputChange,
    onBlur: handleOnBlur
  }), [handleInputChange, handleOnBlur]);
  const walletPenciledAnalyticsHandler = useCallback(() => {
    if (!isPenciledVdpListing) {
      sendClick(myWalletInteractionClick, null, {
        clickType,
        paymentsDuck
      });
      updatePenciledVdpListings(listingId);
      updatePenciledLatestPageId('vdp');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPenciledVdpListing]);
  const enableMyWallet = () => {
    walletPenciledAnalyticsHandler();
    if (!paymentEmphasis) {
      sendClick(myWalletInteractionClick, null, {
        clickType,
        paymentsDuck
      });
      updateUserPaymentInfo();
    }
  };
  const handleEmailCTAClick = context => {
    handleCreateWalletId();
    enableMyWallet();
    onEmailCTAClick(context);
  };
  const handleOnOpeningSDPFailed = context => {
    handleCreateWalletId();
    if (!paymentEmphasis) {
      updateUserPaymentInfo();
    }
    onEmailCTAClick(context);
  };

  // Used by CallsToAction component and prequal link
  const digitalRetailProps = {
    // SDP requires financeListedPrice to include manufacturer rebate.
    listedPrice: isFinanceDeal ? localPrice + (financePricingDetails?.rebate ?? 0) : localPrice,
    listing,
    paymentsInfo: {
      ...paymentsInfo,
      downPayment: financeDownPayment,
      creditType: selectedCreditTier,
      dealType: isLeaseDeal && !leasePayment ? 'finance' : activeTab ?? initialDealType,
      dueAtSigning: dueAtSigningValue,
      activeTab
    },
    onError: () => handleOnOpeningSDPFailed('calculatorOfferSummary'),
    paymentsDuck,
    hideSendOfferCTA,
    parent: 'nds',
    dealerId,
    // mmd ID
    userDuck
  };
  const fetchPaymentDetails = (withTaxesAndFees, applyResults, userZip) => {
    if (enableExternalPaymentFetch && onFetchPersonalizedPaymentDetails) {
      const pencilingData = {
        dealType: activeTab || initialDealType,
        downPayment: financeDownPayment,
        creditTier: obfuscatedCredit,
        leaseTerm: selectedLeaseTerm,
        loanTerm: selectedLoanTerm,
        tradeInValue: localTradeInValue,
        amountOwed: localAmountOwed,
        userZip,
        mileage: selectedMileage
      };
      onFetchPersonalizedPaymentDetails(pencilingData);
    } else if (paymentsEnabled) {
      fetchPersonalizedPayment(withTaxesAndFees, applyResults, userZip);
    }
  };
  const renderTaxesAndFees = function (tabName) {
    let uiContext = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'default';
    return /*#__PURE__*/jsx(TaxesAndFees, {
      uiContext: uiContext,
      isEstimate: digitalRetailingType === 'default',
      enableTaxesAndFees: enableTaxesAndFees && !paymentErrorState,
      tabName: tabName,
      includeTaxesAndFees: includeTaxesAndFees,
      isNationalRates: isNationalRates,
      fetchPaymentDetails: fetchPaymentDetails,
      handleNDSPencilling: handleNDSPencilling,
      zip: zip,
      container: container,
      taxesAndFeesObj: taxesAndFeesObj,
      updatePaymentsInfo: updatePaymentsInfo,
      updatePaymentPencils: updatePaymentPencils,
      digitalRetailingType: digitalRetailingType,
      handleNDSClick: handleNDSClick,
      totalTaxesFees: totalTax,
      setHasTaxesFeesModalOpen: setHasTaxesFeesModalOpen,
      activeTab: activeTab,
      ndsPencilingFeature: ndsPencilingFeature,
      brand: brand,
      setIncludeTaxesAndFees: setIncludeTaxesAndFees,
      setPaymentCalculationDetails: setPaymentCalculationDetails,
      paymentCalculationDetails: paymentCalculationDetails,
      updateUserPaymentInfo: updateUserPaymentInfo,
      capitalizedTaxesAndFees: capitalizedTaxesAndFees
    });
  };
  const getPriceBreakdown = tabName => {
    const _priceBreakdown = {
      cash: {
        ...cashPricingDetails
      },
      lease: {
        ...leasePricingDetails
      }
    }[tabName] || {
      ...financePricingDetails
    };
    _priceBreakdown.manufacturerOffer = _priceBreakdown.rebate;
    _priceBreakdown.offersUpgrades = _priceBreakdown.specialOfferValue;
    return _priceBreakdown;
  };
  const renderPriceBreakdown = tabName => {
    if (!enablePriceBreakdown) {
      return null;
    }
    const hasOffers = !!incentivesUrl && (paymentCalculationDetails?.expectAvailableOffers || listingPaymentDetails?.expectAvailableOffers);
    const priceBreakdown = {
      ...getPriceBreakdown(tabName)
    };
    return /*#__PURE__*/jsx(PricingBreakdown, {
      pricingDetail: priceBreakdown,
      hasOffers: hasOffers,
      ...(hasOffers && {
        incentivesEmailCTA: /*#__PURE__*/jsx(EmailCTA, {
          listingId: listingId,
          type: "link",
          context: "incentives",
          labelKey: "seeAvailableOffers",
          onClick: onEmailCTAClick
        })
      }),
      hideMsrp: !showMsrp,
      disableIncentives: disableIncentives,
      enableOfferUpgrades: enableOfferUpgrades
    }, "pricingBreakdown");
  };

  // To be passed to OfferColumn
  const ctas = /*#__PURE__*/jsx(CallsToAction, {
    activeTab: activeTab ?? initialDealType,
    customCallToAction: customCallToAction,
    saveDealXgFetcher: saveDealXgFetcher // For esntial deal
    ,

    env: env // For saveDeaXgFetcher
    ,

    digitalRetailProps: digitalRetailProps,
    handleEmailCTAClick: handleEmailCTAClick,
    isDrEnabled: isDRAccelerate,
    listing: listing // For esntial deal
    ,

    listingId: listingId,
    listingVin: listingVin,
    nationalRates: nationalRates // For esntial deal
    ,

    paymentsDuck: paymentsDuck,
    dealsDuck: dealsDuck,
    paymentErrorState: paymentErrorState,
    zip: zip,
    hideSendOfferCTA: hideSendOfferCTA,
    creditVerificationHandler: verifyCreditRatingViaEvent
  }, "callsToAction");

  // Clean up props to pass on
  delete rest.inventoryDuck;
  delete rest.dispatch;
  const sharedTabProps = {
    ...rest,
    handleNDSClick,
    formattedTotalPrice,
    inputProps,
    isLoading,
    onSubmit,
    renderPriceBreakdown,
    renderTaxesAndFees,
    tradeInfoInputProps: {
      digitalRetailingType,
      supportingLinks: {
        kbbLink,
        payoffQuoteLink
      },
      valueOwed: localAmountOwed,
      valueTradeIn: localTradeInValue
    },
    previousCompletedInput
  };

  // price breakdown inside Offer Breakdown section
  const getPricingBreakdown = tabName => {
    const pricingBreakdown = {
      ...getPriceBreakdown(tabName)
    };
    if (pricingBreakdown.salePrice) {
      pricingBreakdown.listingPrice = pricingBreakdown.salePrice;
    }
    if (offerPrice) {
      pricingBreakdown.offerPrice = offerPrice;
    }
    pricingBreakdown.manufacturerOffer = pricingBreakdown.rebate;
    pricingBreakdown.offersUpgrades = pricingBreakdown.specialOfferValue;
    return pricingBreakdown;
  };
  const offerBreakdownProps = {
    amountOwed,
    dueAtSigningValue,
    getPricingBreakdown,
    listingId,
    // for analytics
    paymentsInfo,
    // for analytics
    tradeInValue,
    diaTax: diaTaxes,
    diaFee: (diaDealerFees ?? 0) + (diaGovFees ?? 0) + (diaLenderFees ?? 0),
    firstPayment,
    cashDueFromCustomer,
    adjustedCapCost,
    isEstimate: digitalRetailingType === 'default',
    capitalizedTaxesAndFees
  };
  const leaseFinancingProps = {
    creditApp,
    digitalRetailProps,
    enableCreditApp,
    enableMyWallet,
    handleEmailCTAClick,
    hideSendOfferCTA,
    isDRAccelerate,
    isOwnerFinancingSubmitted: !_isEmpty(ownerCreditAppDeal),
    listingId,
    onFinancingCTAClick
  };
  const financingProps = {
    ...leaseFinancingProps,
    ...(showLendingOptions && {
      handleLendingOptionsCTA
    })
  };
  const fallBackNationalInterestRate = {
    name: DEFAULT_RATE_LABEL,
    rate: selectedInterestRate,
    payment: calculatePersonalPayment(localPrice, {
      loanTerm: selectedLoanTerm,
      downPayment: financeDownPayment,
      totalEquity
    }, selectedInterestRate)
  };
  useLendingOptions(paymentCalculationDetails, listingPaymentDetails, obfuscatedCredit, selectedLoanTerm, vehicleCondition, setLendingOptions);
  const creditVerified = useSelector(paymentsDuck?.selectors?.getCreditVerification)?.verified ?? false;
  const getCreditVerificationLink = () => {
    if (creditVerified) {
      return enableCreditVerification ? creditVerifiedIndicator : undefined;
    }
    return creditVerificationLink;
  };
  const financeTab = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(FinanceTab, {
      lendingOptions: lendingOptions,
      creditTierOptions: creditTierOptions,
      creditVerificationLink: getCreditVerificationLink(),
      customDefaultFinanceTerm: customDefaultFinanceTerm,
      onSupportingLinkClick: handleSupportingLinkClick,
      isSupportingLinkClicked: isSupportingLinkClicked,
      financeDownPayment: financeDownPayment,
      handleSelectChange: handleSelectChange,
      selectedCreditTier: selectedCreditTier,
      selectedLoanTerm: selectedLoanTerm,
      showOfferPrice: showOfferPrice,
      ...sharedTabProps
    }), additionalContent]
  });
  const leaseTab = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(LeaseTab, {
      creditTierOptions: creditTierOptions,
      dealType: activeTab,
      dueAtSigningValue: dueAtSigningValue,
      handleSelectChange: handleSelectChange,
      leaseDownPayment: leaseDownPayment,
      leaseMileageOptions: leaseMileageOptions,
      leaseOffersObj: leaseOffersObj,
      leaseTermOptions: leaseTermOptions,
      selectedCreditTier: selectedCreditTier,
      selectedLeaseTerm: selectedLeaseTerm,
      selectedMileage: selectedMileage,
      ...sharedTabProps
    }), additionalContent]
  });
  const cashTab = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(CashTab, {
      showOfferPrice: showOfferPrice,
      ...sharedTabProps
    }), additionalContent]
  });
  const getFinancePayment = () => {
    if (!useCustomRate) {
      return financeOffersObj.payment;
    }
    return Math.max(0, calculatePersonalPayment(localPrice, {
      loanTerm: selectedLoanTerm,
      downPayment: financeDownPayment,
      totalEquity
    }, selectedInterestRate)) || 0;
  };
  const formatPayment = (errorState, payment) => formatCurrency(errorState || !_isNumber(payment) ? '---' : Math.floor(payment));
  const renderTabHeaderContent = tabName => {
    const titles = {
      finance: 'Finance',
      lease: 'Lease',
      cash: 'Cash'
    };
    const dealValues = {
      finance: formatPayment(isFinanceErrorState, getFinancePayment()),
      lease: formatPayment(isLeaseErrorState, leaseOffersObj.payment),
      cash: formatPayment(!localPrice, Math.max(0, totalCashOut))
    };
    return /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Text, {
        color: "subdued-lighter",
        children: titles[tabName]
      }), /*#__PURE__*/jsx("div", {
        className: "margin-top-1",
        children: isLoading ? /*#__PURE__*/jsx(LoadingSpinner, {
          height: 24,
          className: "display-flex align-items-center margin-0 margin-horizontal-auto"
        }) : /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(Text, {
            size: 300,
            weight: "bold",
            children: dealValues[tabName]
          }), tabName !== 'cash' && /*#__PURE__*/jsx(Text, {
            size: 200,
            children: "/mo."
          })]
        })
      })]
    });
  };
  const renderIncentiveIcon = tabName => {
    const tabIncentiveMap = {
      finance: financeOffersObj?.appliedIncentiveIds ?? [],
      cash: cashOffersObj?.appliedIncentiveIds ?? [],
      lease: leaseOffersObj?.appliedIncentiveIds ?? []
    };
    const incentiveIcon = /*#__PURE__*/jsx("svg", {
      width: "11",
      height: "11",
      viewBox: "0 0 11 11",
      children: /*#__PURE__*/jsx("path", {
        d: "M10.8437 6.2457L4.98877 0.370508C4.90203 0.283789 4.72855 0.21875 4.62012 0.21875H1.25897L0.5 0.869141V4.33789C0.5 4.46797 0.565055 4.61973 0.651794 4.70645L6.50671 10.56C6.59345 10.6467 6.72356 10.6467 6.81029 10.56L10.822 6.5709C10.9304 6.48418 10.9304 6.33242 10.8437 6.2457ZM2.01794 2.99375C1.67098 2.64687 1.67098 2.10488 2.01794 1.75801C2.3649 1.41113 2.90702 1.41113 3.25398 1.75801C3.60094 2.10488 3.60094 2.64687 3.25398 2.99375C2.9287 3.31895 2.3649 3.31895 2.01794 2.99375Z",
        fill: "white"
      })
    });
    return tabIncentiveMap[tabName].length > 0 ? incentiveIcon : null;
  };
  const renderTabs = () => {
    const tabContentMap = {
      finance: financeTab,
      lease: leaseTab,
      cash: cashTab
    };
    if (supportedDealTypes.length === 1) {
      return tabContentMap[supportedDealTypes[0]];
    }
    const activeTabKey = activeTab && supportedDealTypes ? supportedDealTypes?.indexOf(activeTab) : 0;
    const selectableTabsContent = supportedDealTypes.map((tabName, index) => TAB_LABELS[tabName] && /*#__PURE__*/jsx(SelectableTabPanel, {
      icon: renderIncentiveIcon(tabName),
      tabHeaderContent: renderTabHeaderContent(tabName),
      inputProps: {
        id: tabName,
        name: 'estimatedPaymentTabs',
        // Should be Removed once we address - US1553680: UDS SelectedTab Dealtype Radio button Not Updating the Checked attribute on the DOM
        ...(activeTabKey === index && {
          'data-cmp': 'activeTab'
        })
      },
      children: tabContentMap[tabName]
    }, `${tabName}-${index}`));
    return /*#__PURE__*/jsx(SelectableTabs, {
      grouped: true,
      defaultActiveKey: activeTabKey,
      onSelect: onTabSelect,
      children: selectableTabsContent
    });
  };
  return /*#__PURE__*/jsx("div", {
    "data-cmp": "ndsTabs",
    children: /*#__PURE__*/jsxs(Form, {
      onValidSubmit: onSubmit,
      className: clsx({
        'row padding-5': !isSingleColumn,
        'padding-3': isSingleColumn
      }),
      children: [/*#__PURE__*/jsxs("div", {
        className: clsx({
          'col-xs-12 col-sm-6': !isSingleColumn
        }),
        children: [/*#__PURE__*/jsx(Heading, {
          size: 600,
          className: clsx({
            'padding-top-4': !!isSingleColumn
          }),
          color: "primary",
          children: "Build Your Offer"
        }), /*#__PURE__*/jsxs(Text, {
          componentClass: "div",
          color: "gray-dark",
          size: 200,
          className: "margin-bottom-5",
          children: ["Powered by Kelley Blue Book", /*#__PURE__*/jsx("sup", {
            children: "\xAE"
          })]
        }), renderTabs()]
      }), /*#__PURE__*/jsxs("div", {
        className: clsx({
          'col-xs-12 col-sm-6 padding-left-sm-3': !isSingleColumn
        }),
        children: [/*#__PURE__*/jsx(OfferColumn, {
          lendingOptions: lendingOptions?.length ? lendingOptions : [fallBackNationalInterestRate],
          container: container,
          ctas: !hideCTAs ? ctas : undefined,
          customRate: customRate,
          dealerFinancingCTA: dealerFinancingCTA,
          dueAtSigningValue: dueAtSigningValue,
          financeDownPayment: financeDownPayment,
          financeOffersObj: financeOffersObj,
          financingCallToAction: /*#__PURE__*/jsx(FinancingCallToAction, {
            ...(isFinanceDeal ? financingProps : leaseFinancingProps)
          }),
          formattedTotalTax: formattedTotalTax,
          handleFallbackPayment: handleFallbackPayment,
          handleLendingOptionsSelection: handleLendingOptionsSelection,
          isLoading: isLoading,
          isPennyPerfectPayment: isPennyPerfectPayment,
          leaseOffersObj: leaseOffersObj,
          listing: listing,
          listingPaymentDetails: listingPaymentDetails,
          localInterestRate: localInterestRate,
          localPrice: localPrice,
          offerBreakdownProps: offerBreakdownProps,
          offerPrice: offerPrice,
          paymentCalculationDetails: paymentCalculationDetails,
          renderTaxesAndFees: renderTaxesAndFees,
          selectedCreditTier: selectedCreditTier,
          selectedLeaseTerm: selectedLeaseTerm,
          selectedLoanTerm: selectedLoanTerm,
          selectedMileage: selectedMileage,
          setShowLendingOptionsModal: setShowLendingOptionsModal,
          showLendingOptionsModal: showLendingOptionsModal,
          hasTaxesFeesModalOpen: hasTaxesFeesModalOpen,
          tabName: activeTab,
          totalCashOut: totalCashOut,
          totalEquity: totalEquity,
          totalFinancing: totalFinancing,
          totalTax: totalTax,
          useCustomRate: useCustomRate,
          vehicleCondition: vehicleCondition,
          paymentErrorState: paymentErrorState
        }), sponsoredInsurance && sponsoredInsurance({
          zip
        })]
      })]
    })
  });
}
function mapStateToProps(state, _ref2) {
  let {
    listingId,
    paymentsDuck
  } = _ref2;
  const {
    inventory = {},
    brand = 'atc',
    payments,
    owners = {}
  } = state;
  const {
    owner: inventoryOwner,
    type = 'used',
    ...restInventory
  } = inventory[listingId] || {};
  const listingPaymentDetails = payments?.listingPayments?.[listingId] || {};
  const pencilingPaymentDetails = payments?.penciling?.pencilingPayments?.[listingId] || null;
  const owner = isObject(inventoryOwner) && inventoryOwner || owners[inventoryOwner] || {};
  const ownerCreditAppDeal = paymentsDuck?.selectors?.getCreditAppDealsByOwnerId?.(state, owner?.id) || {};
  const paymentsInfo = paymentsDuck.selectors.getPaymentsInfo(state) || {};
  const userLocationZip = state?.user?.location?.zip;
  const incentives = paymentsDuck.selectors.getListingIncentives(state) || {};
  const listingType = type.toLowerCase();
  const vehicleCondition = listingType === 'certified' ? 'used' : listingType;
  return {
    brand,
    isLoading: paymentsDuck.selectors.getIsLoading(state),
    listing: {
      ...restInventory,
      type,
      owner
    },
    listingPaymentDetails,
    listingIncentives: incentives[listingId],
    pencilingPaymentDetails,
    paymentsInfo,
    userLocationZip,
    vehicleCondition,
    ownerCreditAppDeal
  };
}
const mapDispatchToProps = (dispatch, _ref3) => {
  let {
    paymentsDuck
  } = _ref3;
  return mapDispatchToActionProp({
    updateICOTradeIn: paymentsDuck.creators.updateICOTradeIn,
    updateIsLoading: paymentsDuck.creators.updateIsLoading,
    updateListingCashPayments: paymentsDuck.creators.updateListingCashPayments,
    updateListingLeasePayments: paymentsDuck.creators.updateListingLeasePayments,
    updateMyWallet: paymentsDuck.creators.updateMyWallet,
    updatePaymentsInfo: paymentsDuck.creators.updatePaymentsInfo,
    updatePaymentPencils: paymentsDuck.creators.updatePaymentPencils,
    updatePencilingPayments: paymentsDuck.creators.updatePencilingPayments,
    updateMyWalletInteractions: paymentsDuck.creators.updateMyWalletInteractions,
    updatePenciledVdpListings: paymentsDuck.creators.updatePenciledVdpListings,
    updatePenciledLatestPageId: paymentsDuck.creators.updatePenciledLatestPageId
  });
};
var NativeDealCalculator$1 = /* #__PURE__*/connect(mapStateToProps, mapDispatchToProps)(/*#__PURE__*/memo(NativeDealCalculator));
export { NativeDealCalculator$1 as default };
